import {
  AccountUser,
  Club,
  ClubDocument,
  ClubPlayer,
  ClubPlayerAcceptedDocument,
  acceptanceMatchesSeries,
  documentRequiredBeforeMembership,
} from "@mixitone/models";
import { withStateToggle } from "@mixitone/util";
import MixitoneController from "controllers/MixitoneController";
import { messagesService } from "services";

interface State {
  loading: boolean;
  recordingPlayerId: string | null;
  club: Club | null;
  latestDocument: ClubDocument | null;
  members: ClubPlayer[];
}

interface Props {
  clubId: string;
  documentName: string;
}

export class DocumentAcceptancesController extends MixitoneController<State, Props> {
  override get initialState(): State {
    return {
      loading: true,
      recordingPlayerId: null,
      club: null,
      latestDocument: null,
      members: [],
    };
  }

  async initialize(props: Props) {
    await this.loadRegister(props.clubId, props.documentName);
  }

  @withStateToggle("loading")
  async loadRegister(clubId: string, documentName: string) {
    const club = await Club.query().find(clubId);
    const documents = await ClubDocument.query()
      .eq("club_id", clubId)
      .eq("name", documentName)
      .orderBy("version", "desc")
      .all();
    const latestDocument = documents.find((document) => !document.is_deleted) ?? null;
    const players = await ClubPlayer.query()
      .eq("club_id", clubId)
      .eq("is_deleted", false)
      .preload("memberships", "accepted_documents")
      .orderBy("name", "asc")
      .all();

    this.setState({
      club,
      latestDocument,
      members: players.filter((player) => player.isCurrentMember),
    });
  }

  acceptanceFor(player: ClubPlayer): ClubPlayerAcceptedDocument | undefined {
    const document = this.state.latestDocument;
    if (!document) return undefined;
    return player.accepted_documents
      .filter((acceptance) => acceptanceMatchesSeries(acceptance, document))
      .toSorted((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
  }

  canRecord(player: ClubPlayer): boolean {
    const document = this.state.latestDocument;
    if (!document || !documentRequiredBeforeMembership(document)) return false;
    return !player.hasAcceptedDocumentVersion(document);
  }

  async actionRecordAcceptance(player: ClubPlayer) {
    const document = this.state.latestDocument;
    if (!document || !this.canRecord(player) || !player.id) return;

    this.state.recordingPlayerId = player.id;
    try {
      const acceptance = new ClubPlayerAcceptedDocument({
        club_player_id: player.id,
        account_id: document.account_id,
        document_name: document.name,
        series_id: document.series_id,
        club_document_id: document.id,
        version: document.version,
        source: "admin",
        recorded_by_user_id: AccountUser.current?.user_id,
        accepted_at: new Date().toISOString(),
      });
      await acceptance.save();
      player.accepted_documents.add(acceptance);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not record acceptance";
      messagesService.addMessage(message, "error");
    } finally {
      this.state.recordingPlayerId = null;
    }
  }
}
